import React, { useEffect, useState } from 'react';
import { List, ListInner, TableStyled, Title, TitleWrapper } from 'app/Pages/common/List/styles';
import Pagination from 'app/Components/common/Pagination';
import { makeRequestQuery, preflightAPICall, processAPIError, translate } from 'utils';
import axios, { AxiosError } from 'axios';
import { TError, TFetchStatus } from 'models';
import { TBroker } from 'app/Pages/common';
import Drawer from './drawer';
import { Tooltip } from '@insly/qmt-reactjs-ui-lib';
import { DefaultIcon } from 'styles/common';
import { API_GROUP_LIST_URL } from 'config/api';
import { createRoles } from 'app/Pages/Broker';

const DOMAIN_TAG = 'branch';

export type TBrokerGroup = {
  id?: string,
  name: string,
  title: string,
  parent_id?: string,
  domain_tag?: string,
};

interface IProps {
  profile: TBroker,
}

interface IListProps {
  fetchStatus: TFetchStatus,
  noResultsText: string,
  userList: TGroupListData,
  handleListChangePage: (page: number) => void,
  handleSearchChange?: (value: string) => void,
  selectedBrokerGroup: TBrokerGroup | null,
  updateSelectedBrokerGroup: (brokerGroup: TBrokerGroup) => void,
  drawerType: string,
}

export type TGroupListData = {
  last_page?: number,
  page?: number,
  results?: TBrokerGroup[],
  total?: number,
  errors?: TError[],
};

const Structure = ({ profile }: IProps) => {
  const [fetchStatus, updateFetchStatus] = useState<TFetchStatus>('loading');
  const [itemList, updateItemList] = useState<TGroupListData>({});
  const [noResultsText, updateNoResultsText] = useState('');
  const [searchQuery, updateSearchQuery] = useState('');
  const [selectedBrokerGroup, updateSelectedBrokerGroup] = useState<TBrokerGroup | null>(null);
  const [drawerType, updateDrawerType] = useState('profile');

  useEffect(() => {
    getList({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const getList = ({ page }: { page?: number }) => {
    updateFetchStatus('loading');
    let query: Record<string, string | number> = {};

    query.page_size = 50;

    if (page) {
      query.page = page;
    }

    if (searchQuery) {
      query.search = encodeURIComponent(searchQuery);
    }

    if (profile.group_id) {
      query.parent_id = profile.group_id;
    }

    query.domain_tag = DOMAIN_TAG;

    const queryString = makeRequestQuery(query);

    preflightAPICall(() => {
      axios.get(`${API_GROUP_LIST_URL}${queryString}`).then(response => {
        const data = response.data;
        updateItemList(data);
        updateFetchStatus('success');

      }).catch(error => {
        updateFetchStatus('failed');
        updateItemList({
          results: [],
        });

        updateNoResultsText(() => processAPIError(error) as string);

      });
    });
  };

  const handleListChangePage = (page: number) => {
    getList({ page });
  };

  const handleDrawerClose = () => {
    updateSelectedBrokerGroup(null);
    if (drawerType !== 'profile') {
      updateDrawerType('profile');
    }
  };

  const handlePatchBrokerGroup = (data: TBrokerGroup, onError: (error: AxiosError) => void) => {
    handlePatchGroup(data, selectedBrokerGroup?.id || '', onError, (brokerGroup) => {
      let list = itemList.results ? [...itemList.results] : [];
      const brokerIndex = list.findIndex(i => i.id === brokerGroup.id);
      if (brokerIndex !== -1) {
        list[brokerIndex] = brokerGroup;
      }
      updateSelectedBrokerGroup(null);
      updateItemList({
        ...itemList,
        results: list
      });
      updateDrawerType('profile');
    });
  };

  const handleDeleteBrokerGroup = () => {

  };

  const handleAddBrokerGroup = (data: TBrokerGroup, onError: (error: AxiosError) => void,) => {
    handleAddGroup(data, DOMAIN_TAG, onError, (brokerGroup) => {
      let list = itemList.results ? [brokerGroup, ...itemList.results] : [brokerGroup];
      updateSelectedBrokerGroup(brokerGroup);
      updateItemList({
        ...itemList,
        results: list
      });
      updateDrawerType('profile');
    });
  };

  const showAddDrawer = () => {
    updateSelectedBrokerGroup(null);
    updateDrawerType('add');
  };

  return (
    <div>
      <TitleWrapper>
        <Title>{translate({ key: 'master.broker_group.branch.title' })}</Title>
        <Tooltip align="top" message={translate({ key: 'master.broker_group.branch.add' })}>
          <DefaultIcon icon="add_user" onClick={showAddDrawer} />
        </Tooltip>
      </TitleWrapper>
      <ListComponent
        handleSearchChange={updateSearchQuery}
        noResultsText={noResultsText}
        fetchStatus={fetchStatus}
        userList={itemList}
        handleListChangePage={handleListChangePage}
        selectedBrokerGroup={selectedBrokerGroup}
        updateSelectedBrokerGroup={updateSelectedBrokerGroup}
        drawerType={drawerType}
      />
      <Drawer
        type={drawerType}
        selectedBrokerGroup={selectedBrokerGroup}
        brokerGroupId={selectedBrokerGroup ? selectedBrokerGroup.id : ''}
        parentId={profile.group_id as string}
        handleDrawerClose={handleDrawerClose}
        dataStatus={fetchStatus}
        handlePatchBrokerGroup={handlePatchBrokerGroup}
        handleDeleteBrokerGroup={handleDeleteBrokerGroup}
        handleAddBrokerGroup={handleAddBrokerGroup}
        updateSelectedBrokerGroup={updateSelectedBrokerGroup}
      />
    </div>
  );

};

const ListComponent = ({
  handleSearchChange,
  noResultsText,
  fetchStatus,
  userList,
  handleListChangePage,
  selectedBrokerGroup,
  updateSelectedBrokerGroup,
  drawerType,
} : IListProps) => (
  <List step="list" noPadding={true}>
    <ListInner>
      <TableStyled
        dataStatus={fetchStatus as string}
        data={userList.results ? userList.results as never[] : undefined}
        noResultsText={noResultsText || translate({ key: 'common.no_results' })}
        onClickRow={drawerType === 'profile' ? (index, data: TBrokerGroup) => updateSelectedBrokerGroup(data) : () => {}}
        selectedId={selectedBrokerGroup ? selectedBrokerGroup.id : undefined}
        handleSearchChange={handleSearchChange}
        columns={[
          {
            name: 'title',
            onRender: renderTitleCol,
            styles: {
              flex: 1
            }
          },
          {
            name: 'name',
            onRender: renderNameCol,
            styles: {
              flex: 1
            }
          },
        ]}
      />
      {userList?.last_page && userList.last_page > 1 ? Pagination({ page: userList.page as number, last_page: userList.last_page }, handleListChangePage) : null}
    </ListInner>
  </List>
);

const renderNameCol = (row: TBrokerGroup) => <>{row.name}</>;
const renderTitleCol = (row: TBrokerGroup) => <>{row.title}</>;

export const handleAddGroup = (data: TBrokerGroup, domainTag: string, onError: (error: AxiosError) => void, onSuccess: (data: TBrokerGroup) => void) => {
  preflightAPICall(() => {
    axios.post(API_GROUP_LIST_URL, {
      ...data,
      domain_tag: domainTag
    }).then(response => {
      const brokerGroup = response.data;

      //Creating roles
      createRoles(brokerGroup.id, () => {
        onSuccess(brokerGroup);
      }, onError);

    }).catch(onError);
  });
};

export const handlePatchGroup = (data: TBrokerGroup, brokerGroupId: string, onError: (error: AxiosError) => void, onSuccess: (data: TBrokerGroup) => void) => {
  preflightAPICall(() => {
    axios.patch(`${API_GROUP_LIST_URL}/${brokerGroupId}`, {
      ...data,
    }).then(response => {
      const brokerGroup = response.data;
      onSuccess(brokerGroup);
    }).catch(onError);
  });
};

export default Structure;
